import { Component, Inject, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form, maxLength, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { TilitapahtumanKanava, TilitapahtumanTila } from '@lemon/core-firebase/model/tiliote'
import { OptionLabelsService } from '@lemon/core-firebase/service/option-labels.service'
import { DateService } from '@lemon/core/service/date.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { AsiakkaanMaksutapa } from 'app/_jaettu-lemonator/model/asiakas'
import { LisaaTilitapahtumaManuaalisestiPyynto, LisaaTilitapahtumaManuaalisestiVastaus } from 'app/_jaettu-lemonator/model/kirjanpito'

export interface LisaaTilitapahtumaDialogData {
  asiakasAvain: string
  kaikkiMaksutavat: AsiakkaanMaksutapa[]
}

interface LisaaTilitapahtumaLomake {
  maksutapa: AsiakkaanMaksutapa | null
  pvm: Date | null
  summa: number | null
  tila: TilitapahtumanTila
  kanava: TilitapahtumanKanava
  valuutta: string
  viite: string
  maksaja: string
  viesti: string
  maksajanIban: string
  lisatiedot: string
  nippuId: string
}

@Component({
  templateUrl: './lisaa-tilitapahtuma.dialog.html',
  styleUrls: ['./lisaa-tilitapahtuma.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LisaaTilitapahtumaDialog {
  lomake: FieldTree<LisaaTilitapahtumaLomake>
  kaikkiMaksutavat: AsiakkaanMaksutapa[]

  tilitapahtumanTilaOptions = this._enumLabelsService.tilitapahtumanTilaOptions('fi')
  tilitapahtumanKanavaOptions = this._enumLabelsService.tilitapahtumanKanavaOptions('fi')

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: LisaaTilitapahtumaDialogData,
    private _dialogRef: MatDialogRef<LisaaTilitapahtumaDialog>,
    private _formValidationService: FormValidationService,
    private _ladataanService: LadataanService,
    private _snackbar: MatSnackBar,
    private _firebaseLemonator: FirebaseLemonator,
    private _dateService: DateService,
    private _enumLabelsService: OptionLabelsService,
  ) {
    // Alustetaan maksutavat
    this.kaikkiMaksutavat = this._data.kaikkiMaksutavat

    // Tämä lomake on testausta varten joten ei validoida pakollisuutta tarkemmin
    this.lomake = form(
      signal<LisaaTilitapahtumaLomake>({
        maksutapa: null,
        pvm: new Date(),
        summa: null,
        tila: 'final',
        kanava: 'aiia',
        valuutta: 'EUR',
        viite: '',
        maksaja: '',
        viesti: '',
        maksajanIban: '',
        lisatiedot: '',
        nippuId: '',
      }),
      polku => {
        required(polku.maksutapa)
        required(polku.pvm)
        required(polku.summa)
        required(polku.tila)
        required(polku.kanava)
        required(polku.valuutta)
        maxLength(polku.valuutta, 3)
      },
    )
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  async luoTilitapahtuma() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this._ladataanService.aloitaLataaminen()

    try {
      const maksutapa = this.lomake.maksutapa().value()
      const pvm = this.lomake.pvm().value()
      const summa = this.lomake.summa().value()
      const iban = maksutapa?.sahkoisenTiliotteenTunniste || ''
      const maksaja = this.lomake.maksaja().value() || ''
      const viesti = this.lomake.viesti().value() || ''
      const viite = this.lomake.viite().value() || ''
      const maksajanIban = this.lomake.maksajanIban().value() || ''
      const valuutta = this.lomake.valuutta().value() || 'EUR'
      const tila = this.lomake.tila().value() || 'final'
      const kanava = this.lomake.kanava().value() || 'aiia'
      const lisatiedot = this.lomake.lisatiedot().value() || ''
      const nippuId = this.lomake.nippuId().value() || ''

      const pvmNumber = this._dateService.dateToNumber(pvm)

      const pyynto: LisaaTilitapahtumaManuaalisestiPyynto = {
        asiakasAvain: this._data.asiakasAvain,
        pvm: pvmNumber,
        summa: summa,
        iban: iban,
        maksaja: maksaja,
        viesti: viesti,
        viite: viite,
        maksajanIban: maksajanIban,
        valuutta: valuutta,
        tila: tila as TilitapahtumanTila,
        kanava: kanava as TilitapahtumanKanava,
        lisatiedot: lisatiedot,
        nippuId: nippuId,
      }

      const vastaus = await this._firebaseLemonator.functionsCall<LisaaTilitapahtumaManuaalisestiPyynto, LisaaTilitapahtumaManuaalisestiVastaus>('lisaaTilitapahtumaManuaalisesti', pyynto)

      if (vastaus?.tilitapahtumaAvain) {
        this._snackbar.open('Tilitapahtuma lisätty onnistuneesti', 'OK', { duration: 3000 })
        this._ladataanService.lopetaLataaminen()
        this._dialogRef.close(true)
      }
    } catch (error) {
      console.error('Tilitapahtuman lisäys epäonnistui', error)
      this._snackbar.open('Tilitapahtuman lisäys epäonnistui', 'OK', { duration: 5000 })
      this._ladataanService.lopetaLataaminen()
    }
  }
}
